/**
 * Banner image from API
 */
export interface BannerImage {
  id: number;
  bannerId: number;
  imageId: number;
  imageUrl?: string;
  aspectRatio: string;
  languageCode: string | null;
  type: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Banner image input for create/edit
 */
export interface BannerImageInput {
  imageId: number;
  imageUrl?: string;
  aspectRatio: string;
  languageCode: string | null;
  type: 'background' | 'logo';
  order: number;
}

/**
 * Banner entity from API
 */
export interface Banner {
  id: number;
  linkUrl: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  viewsCount: number;
  clicksCount: number;
  uniqueViewsCount: number;
  uniqueClicksCount: number;
  createdAt: string;
  updatedAt: string;
  images: BannerImage[];
}

/**
 * Banner form data for create/update
 */
export interface BannerFormData {
  images: BannerImageInput[];
  linkUrl: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

/**
 * Device-specific image slots for banners.
 * Matches platform BannerCarousel breakpoints:
 *   mobile / tablet (default) → 16:7
 *   lg (1024px+)              → 21:9
 * Старые баннеры с картинкой 16:9 продолжают работать: платформа берёт 16:7,
 * а при его отсутствии — legacy 16:9.
 */
export const DEVICE_SLOTS = [
  { aspectRatio: '21:9', label: 'Desktop', description: '2560×1097' },
  { aspectRatio: '16:7', label: 'Mobile / Tablet', description: '1024×448' },
] as const;

/**
 * Get first background image URL from banner
 */
export function getBannerBackgroundUrl(banner: Banner): string | null {
  const images = banner.images ?? [];
  const bgImage = images.find(img => img.type === 'background');
  return bgImage?.imageUrl ?? images[0]?.imageUrl ?? null;
}

/**
 * Get first background image file record from banner
 */
export function getBannerBackgroundFile(banner: Banner): { id: number; url: string } | null {
  const images = banner.images ?? [];
  const bgImage = images.find(img => img.type === 'background') ?? images[0];
  if (!bgImage) {return null;}
  return { id: bgImage.imageId, url: bgImage.imageUrl ?? '' };
}

/**
 * Calculate CTR (Click-Through Rate)
 */
export function calculateCTR(banner: Banner): number {
  if (banner.viewsCount === 0) {return 0;}
  return (banner.clicksCount / banner.viewsCount) * 100;
}
